/* eslint-disable no-plusplus */
import React, { useContext, useEffect, useState, useCallback } from "react";
import { area } from "@turf/turf";
import { format } from "date-fns";
// eslint-disable-next-line
import mapboxgl from "!mapbox-gl";
import { MapActions, MapContext } from "context/map";
import { UserContext } from "context/user";
import { getHarvestMoisture } from "api/HarvestMoisture";
import Loading from "components/Loading";
import "./index.scss";

const HarvestMoisture = () => {
  const { token } = useContext(UserContext);
  const { state, dispatch } = useContext(MapContext);
  const { map, inspect, selectedFields, harvestMoistureData } = state;
  const [riceHarvestMoistureData, setRiceHarvestMoistureData] = useState([]);
  const [legendEntries, setLegendEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [forecastDate, setForecastDate] = useState();
  const dayMs = 86400000;

  const fetchHarvestMoistureData = useCallback(async () => {
    try {
      const query = selectedFields
        .map((field) => `field_ids=${field}`)
        .join("&");
      const response = await getHarvestMoisture(query, token);
      const zoneData = response.map((item) => {
        const parsedZones = JSON.parse(item.zones);

        return { zones: parsedZones };
      });
      if (zoneData.length && zoneData[0].zones.date) {
        const date = new Date(zoneData[0].zones.date);
        setForecastDate(date);
      }
      const zoneFeatures = zoneData.map((item) => item.zones.features);
      const flattenZoneFeatures = zoneFeatures.flat();
      const sortedZoneFeatures = flattenZoneFeatures.sort(
        (a, b) =>
          new Date(b.properties.Forecast) - new Date(a.properties.Forecast)
      );
      const pastDates = flattenZoneFeatures.filter(
        (zoneDate) => new Date(zoneDate.properties.Forecast) < new Date()
      );
      if (pastDates.length > 0) {
        setIsReady(true);
      } else {
        setIsReady(false);
      }
      setRiceHarvestMoistureData(sortedZoneFeatures);
      dispatch({
        type: MapActions.SET_HARVEST_MOISTURE_DATA,
        value: sortedZoneFeatures,
      });
    } catch (e) {
      dispatch({
        type: MapActions.SET_HARVEST_MOISTURE_DATA,
        value: null,
      });
      setIsLoading(false);
    }
  }, [selectedFields, token, dispatch]);

  const getLegendLabel = (label, days, isLastRow) => {
    const daysRange = days <= 5 ? 1 : Math.ceil((days - 5) / 5);
    const getRangeLabel = (start, end) =>
      start !== end ? `${start}-${end} Days` : `${start} Days`;

    if (label === 0) {
      return days <= 5 ? getRangeLabel(1, 1) : getRangeLabel(1, 5);
    }

    if (isLastRow) {
      return days <= 5
        ? `More than ${label * daysRange} days`
        : `More than ${5 + (label - 1) * daysRange} days`;
    }

    const startRange =
      days <= 5 ? label * daysRange + 1 : 6 + (label - 1) * daysRange;
    const endRange = startRange + daysRange - 1;

    return getRangeLabel(startRange, endRange);
  };

  const getLegendColor = (entry) => {
    const colors = {
      0: "#E8991A",
      1: "#E8D41A",
      2: "#1DCF2F",
      3: "#0C7931",
      4: "#0C796C",
      5: "#0C4B79",
    };

    return colors[entry];
  };

  const withinFiveDaysData = useCallback((currentDate, remainingDays) => {
    const dateArray = [];
    let daysLeft = remainingDays;
    const dayRangeMs = 86399999;
    const startMs =
      new Date(new Date().setUTCHours(0, 0, 0, 0)).getTime() + 50400000;

    for (let i = 0; i < 6 && daysLeft >= 0; i++) {
      let rowStartDate = new Date();
      if (i === 0) {
        rowStartDate = new Date(startMs);
      } else {
        rowStartDate = new Date(dateArray[i - 1].endDate.getTime() + 1);
      }
      const rowEndDate = new Date(rowStartDate.getTime() + dayRangeMs);
      daysLeft -= 1;
      const isLastRow = daysLeft < 0 || i === 5;

      const row = {
        startDate: rowStartDate,
        endDate: rowEndDate,
        id: i,
        label: getLegendLabel(i, remainingDays, isLastRow),
        color: getLegendColor(i),
      };
      dateArray.push(row);
    }
    return dateArray;
  }, []);

  const generateRowData = useCallback((remainingDays, latestPredictionDate) => {
    const dateArray = [];
    let daysLeft = remainingDays;
    const daysRange = Math.ceil((daysLeft - 5) / 5);
    const dayRangeMs = daysRange * dayMs - 1;
    const startMs =
      new Date(new Date().setUTCHours(0, 0, 0, 0)).getTime() + 50400000;

    const firstRow = {
      startDate: new Date(startMs),
      endDate: new Date(startMs + (dayMs * 5 - 1)),
      i: 0,
      label: getLegendLabel(0, remainingDays),
      color: getLegendColor(0),
    };
    dateArray.push(firstRow);

    daysLeft -= 5;
    for (let i = 0; i < 5 && daysLeft >= 0; i++) {
      const rowIndex = i + 1;
      const rowStartDate = new Date(dateArray[i].endDate.getTime() + 1);
      const rowEndDate = new Date(rowStartDate.getTime() + dayRangeMs);

      daysLeft -= daysRange;
      const isLastRow = daysLeft < 0 || i === 4;

      const row = {
        startDate: rowStartDate,
        endDate: isLastRow
          ? new Date(latestPredictionDate + dayMs)
          : rowEndDate,
        id: rowIndex,
        label: getLegendLabel(rowIndex, remainingDays, isLastRow),
        color: getLegendColor(rowIndex),
      };
      dateArray.push(row);
    }

    return dateArray;
  }, []);

  const beyondFiveDaysData = useCallback(
    (currentDate, remainingDays, latestPredictionDate) => {
      const upcomingDays = new Date(currentDate);
      const maxDays = Math.min(remainingDays, 35);
      upcomingDays.setDate(upcomingDays.getDate() + maxDays);

      const remainingRowsData = generateRowData(maxDays, latestPredictionDate);

      return [...remainingRowsData];
    },
    [generateRowData]
  );

  const calculateLegendEntries = useCallback(
    (latestPredictionDate) => {
      const currentDate = new Date(
        new Date(new Date().setUTCHours(0, 0, 0, 0)).getTime() + 50400000
      );
      const timeDifference =
        latestPredictionDate.getTime() - currentDate.getTime();
      const remainingDays = Math.ceil(timeDifference / (1000 * 3600 * 24));
      if (remainingDays <= 5) {
        const entries = withinFiveDaysData(currentDate, remainingDays);

        return entries;
      }

      const entries = beyondFiveDaysData(
        currentDate,
        remainingDays,
        latestPredictionDate.getTime()
      );
      return entries;
    },
    [beyondFiveDaysData, withinFiveDaysData]
  );

  const generatePrediction = useCallback(() => {
    const latestPredictionDate = new Date(
      riceHarvestMoistureData[0].properties.Forecast
    );
    const entries = calculateLegendEntries(latestPredictionDate);

    setLegendEntries(entries);
  }, [calculateLegendEntries, riceHarvestMoistureData]);

  const initializeHarvestMoistureSource = useCallback(
    (features) => {
      const source = map.getSource("harvest-moisture-source");

      if (source) {
        source.setData({
          type: "FeatureCollection",
          features,
        });

        return;
      }

      map.addSource("harvest-moisture-source", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features,
        },
        generateId: true,
      });
    },
    [map]
  );

  const initializeHarvestMoistureLayer = useCallback(() => {
    const layerFill = map.getLayer("harvest-moisture-layer-fill");
    const activeLayerOutline = map.getLayer("harvest-moisture-layer-outline");

    if (!layerFill) {
      map.addLayer({
        id: "harvest-moisture-layer-fill",
        type: "fill",
        source: "harvest-moisture-source",
        paint: {
          "fill-color": ["get", "color"],
          "fill-outline-color": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            "#FFFFFF",
            "transparent",
          ],
        },
      });
    }

    if (!activeLayerOutline) {
      map.addLayer({
        id: "harvest-moisture-layer-outline",
        type: "line",
        source: "harvest-moisture-source",
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
        paint: {
          "line-color": [
            "case",
            ["boolean", ["feature-state", "active"], false],
            "#000000",
            "#FFFFFF",
          ],
          "line-width": 2,
        },
      });
    }
  }, [map]);

  useEffect(() => {
    setIsLoading(true);
    fetchHarvestMoistureData();
  }, [fetchHarvestMoistureData]);

  useEffect(() => {
    if (riceHarvestMoistureData.length) {
      generatePrediction();
      setIsLoading(false);
    }
  }, [generatePrediction, riceHarvestMoistureData.length]);

  useEffect(() => {
    const popup = new mapboxgl.Popup();
    const handleClick = (e) => {
      if (!inspect) return;

      const coordinates = e.lngLat;
      const featureArea =
        Math.round((area(e.features[0].geometry) / 1000) * 10) / 100;
      const harvestDate = format(
        new Date(e.features[0].properties.Forecast),
        "MMMM d, yyyy"
      );
      const formattedForecastDate = forecastDate
        ? format(forecastDate, "MMMM d, yyyy")
        : "";
      const confidenceLevel = e.features[0].properties.Confidence;
      const dueIn = e.features[0].properties.harvestLabel;
      const html = `
      <div class="map-layer-details">
        <div class="harvest-due">
          <div class="label">${dueIn === "" ? "Ready" : `Due in ${dueIn}`}</div>
        </div>
        <div class="harvest-header">Harvest Prediction</div>
        <div class="harvest-item">
          <label>Zone Size:</label>
          <div>${featureArea} ha</div>
        </div>
        <div class="harvest-item">
          <label>Est. Harvest Date:</label>
          <div>${harvestDate}</div>
        </div>
        <div class="harvest-item">
          <label>Image Date:</label>
          <div>${formattedForecastDate}</div>
        </div>
        <div class="harvest-item">
          <label>Confidence Level:</label>
          <div>${confidenceLevel}</div>
        </div>
      </div>
    `;
      map.setFeatureState(
        { source: "harvest-moisture-source", id: e.features[0].id },
        { active: true }
      );
      popup.setLngLat(coordinates).setHTML(html).addTo(map);
    };

    if (map && legendEntries.length) {
      const features = riceHarvestMoistureData.map((item) => {
        const forecast = new Date(item.properties.Forecast);
        const legend = legendEntries.find(
          (entry) =>
            new Date(entry.startDate) <= forecast &&
            new Date(entry.endDate) >= forecast
        );
        const color = legend?.color || "#CCCCCC";
        const harvestLabel = legend?.label || "";

        return {
          ...item,
          properties: { ...item.properties, harvestLabel, color },
        };
      });

      initializeHarvestMoistureSource(features);
      initializeHarvestMoistureLayer();

      map.on("click", "harvest-moisture-layer-fill", handleClick);
      popup.on("close", () => {
        map.removeFeatureState({ source: "harvest-moisture-source" });
      });
    }

    return () => {
      const layerFill = map.getLayer("harvest-moisture-layer-fill");
      const activeLayerOutline = map.getLayer("harvest-moisture-layer-outline");

      if (layerFill) {
        map.removeLayer("harvest-moisture-layer-fill");
      }
      if (activeLayerOutline) {
        map.removeLayer("harvest-moisture-layer-outline");
      }

      map.off("click", "harvest-moisture-layer-fill", handleClick);
      popup.remove();
    };
  }, [
    initializeHarvestMoistureLayer,
    initializeHarvestMoistureSource,
    inspect,
    legendEntries,
    map,
    riceHarvestMoistureData,
    forecastDate,
  ]);

  const renderLegendEntries = () => {
    if (!harvestMoistureData?.length) {
      return null;
    }
    return (
      <div className="harvest-moisture-legend-container">
        <h2 className="header">Days Until Harvest</h2>
        {isLoading ? (
          <Loading className="loader" />
        ) : (
          <>
            <ul className="legend-entries-wrapper">
              {isReady && (
                <div className="container">
                  <div
                    className="legend-color"
                    style={{ backgroundColor: "#CCCCCC" }}
                  />
                  <li className="legend-item">Ready</li>
                </div>
              )}
              {legendEntries.map((entry) => (
                <div className="container" key={entry.id}>
                  <div
                    className="legend-color"
                    style={{ backgroundColor: entry.color }}
                  />
                  <li key={entry} className="legend-item">
                    {entry.label}
                  </li>
                </div>
              ))}
            </ul>
            <div className="footer">
              Harvest forecast dates are modelled, and accuracy may vary by up
              to 4 days. The confidence rating will indicate the stability of
              the field&lsquo;s forecasting.
            </div>
          </>
        )}
      </div>
    );
  };

  return <>{renderLegendEntries()}</>;
};

export default HarvestMoisture;
